import { MentionData } from "@draft-js-plugins/mention";
import { Loading, useDataMutation } from "@lyfegen-platform/react-common";
import { Box, IconButton } from "@material-ui/core";
import { Send } from "@material-ui/icons";
import { convertToRaw, EditorState } from "draft-js";
import { useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { SupportedMessageNodes } from "../../../models/generated/graphql";
import { initialNodeInputMessageState } from "../../../store/message";
import { userPersonalInfoState } from "../../../store/user";
import CustomMessageInput from "./CustomMessageInput";
import { SAVE_MESSAGE } from "./queries";
import useMessagesStyles from "./use-messages-styles";
import { SaveMessageMutation, SaveMessageMutationVariables } from "./types/queries.generated";
import LyfeAvatar from "../../../components/LyfeAvatar";

interface MessageInputProps {
	nodeId: string;
	nodeLabel: SupportedMessageNodes;
	isExposure?: boolean;
	externalFlowDocumentId?: string;
}

const MessageInput: React.FC<MessageInputProps> = ({ nodeId, nodeLabel, isExposure, externalFlowDocumentId }) => {
	const { sendIcon } = useMessagesStyles();
	const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
	const userData = useRecoilValue(userPersonalInfoState);
	const [input, setInput] = useRecoilState(initialNodeInputMessageState(nodeId));

	const [save, { loading }] = useDataMutation<SaveMessageMutation>(SAVE_MESSAGE, {
		onCompleted: (data) => data && setInput(""),
	});

	const canSendMessage = !input?.trim() || input.length > 1999;

	const getMentions = (): Array<string | null> => {
		const contentState = editorState.getCurrentContent();
		const rawMessageObject = convertToRaw(contentState);

		let mentions: Array<string | null> = [];

		for (const key in rawMessageObject.entityMap) {
			const ent = rawMessageObject.entityMap[key];
			if (ent.type === "mention") {
				const mentionedUser: MentionData = ent.data.mention;
				if (!mentions?.includes(mentionedUser.link || null)) {
					mentions = [...mentions, mentionedUser.link || null];
				}
			}
		}

		return mentions;
	};

	const handleSave = () => {
		if (!input) return;

		const mentions: Array<string | null> = getMentions();
		const variables: SaveMessageMutationVariables = {
			input: {
				content: editorState.getCurrentContent().getPlainText(),
				id: nodeId,
				nodeLabel,
				sentByUserId: userData.id,
				mentionsUserIds: mentions,
				isExternalFlow: isExposure,
				externalFlowDocumentId: externalFlowDocumentId,
			},
		};
		save({ variables });

		setEditorState(() => EditorState.createEmpty());
	};

	return (
		<Box display="flex" alignItems="center" justifyContent="space-between">
			<Box display="flex" alignItems="center" width="100%">
				<LyfeAvatar
					hideTooltip
					size="small"
					userId={userData.id}
					lastName={userData.lastName || ""}
					firstName={userData.firstName || ""}
				/>
				<CustomMessageInput
					currentUserEmail={userData.email}
					nodeId={nodeId}
					nodeLabel={nodeLabel}
					editorstates={{
						editorState: editorState,
						setEditorState: setEditorState,
					}}
					handleSend={handleSave}
				/>
			</Box>
			<IconButton disabled={canSendMessage} disableRipple classes={{ root: sendIcon }} onClick={handleSave}>
				{loading ? (
					<Loading size="1.5rem" />
				) : (
					<Send
						color="primary"
						style={{
							opacity: canSendMessage ? 0.5 : 1,
						}}
					/>
				)}
			</IconButton>
		</Box>
	);
};

export default MessageInput;
